/* eslint-disable @typescript-eslint/no-use-before-define */

// Based on https://github.com/lucafalasco/scroll-snap, MIT License, Copyright (c) 2017 Luca Falasco
//
// Unfortunately We had to customize the original implementation a lot, so not realistic to submit a
// PR to the original project:
// The original library supports scroll-snap for one container and will then snap page by page, with
// a fixed page height like for example "90vh".
// Our customized version supports snapping based on placeholder containers instead. And it supports
// mixing free native scrolling (for sections without any placeholder containers) and snapping
// (for sections with placeholder containers) on the same page.

import ShadowDom from '@/common/ts/shadow-dom/ShadowDom';

function easeInOutQuad (t: number) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

const DEBUG = false;
const TIMEOUT_MIN = 50;
const TIMEOUT_DEFAULT = 100;
const DURATION_DEFAULT = 300;
const THRESHOLD_DEFAULT = 0.15;
const SNAP_STOP_DEFAULT = true;
const EASING_DEFAULT = easeInOutQuad;
// eslint-disable-next-line @typescript-eslint/no-empty-function
const NOOP = () => {};

let scrollSnapInstance: ScrollSnapInstance;

export interface ScrollSnapInstance {
  log: Function;
  bind: Function;
  unbind: Function;
  addSnapDestinationYElements: Function;
}

interface Settings {
  /**
   * snap-destination for x and y axes
   * should be a valid css value expressed as px|%|vw|vh
   */
  snapDestinationX?: string | number;
  snapDestinationY?: string | number;
  snapDestinationYElements?: HTMLElement[];

  /**
   * time in ms after which scrolling is considered finished
   */
  timeout?: number;

  /**
   * duration in ms for the smooth snap
   */
  duration?: number;

  /**
   * threshold to reach before scrolling to next/prev element, expressed as a percentage in the range [0, 1]
   */
  threshold?: number;

  /**
   * when true, the scroll container is not allowed to "pass over" the other snap positions
   */
  snapStop?: boolean;

  /**
   * custom easing function
   * @param t normalized time typically in the range [0, 1]
   */
  easing?: (t: number) => number;
}

interface SnapLength {
  value: number;
  unit: string;
}

interface SnapCoordinates {
  y: SnapLength;
  x: SnapLength;
}

interface Coordinates {
  y?: number;
  x?: number;
}

export function getOrCreateScrollSnap (
  element: HTMLElement,
  settings: Settings = {},
  callback?: () => void,
): ScrollSnapInstance {
  if (scrollSnapInstance) {
    if (settings.snapDestinationYElements) {
      scrollSnapInstance.log('Adding elements to existing instance: ', settings.snapDestinationYElements);
      scrollSnapInstance.addSnapDestinationYElements(settings.snapDestinationYElements);
    }
    return scrollSnapInstance;
  }

  scrollSnapInstance = createScrollSnap(element, settings, callback);
  return scrollSnapInstance;
}

export default function createScrollSnap (
  element: HTMLElement,
  settings: Settings = {},
  callback?: () => void,
) {
  log('Creating new instance: ', settings);

  const onAnimationEnd = typeof callback === 'function' ? callback : NOOP;

  let listenerElement: HTMLElement | Window;
  let target: HTMLElement;
  let animating = false;

  let scrollHandlerTimer: number;
  let scrollSpeedTimer: number;
  let scrollStart: Coordinates;
  let speedDeltaX: number;
  let speedDeltaY: number;
  let snapLengthUnit: SnapCoordinates;
  const lastScrollValue: Coordinates = {
    x: 0,
    y: 0,
  };
  let animationFrame: number;

  const { snapDestinationX, snapDestinationY } = settings;
  let snapDestinationYElements = settings.snapDestinationYElements
    ? [...settings.snapDestinationYElements] // clone array, don't use reference!
    : undefined;
  let currentSnapDestinationElement: HTMLElement | undefined;
  let prevSnapDestinationElement: HTMLElement | undefined;
  let nextSnapDestinationElement: HTMLElement | undefined;

  // any value less than TIMEOUT_MIN may cause weird behaviour on some devices (especially on mobile with momentum scrolling)
  const timeout = settings.timeout && settings.timeout >= TIMEOUT_MIN ? settings.timeout : TIMEOUT_DEFAULT;
  const duration = settings.duration || DURATION_DEFAULT;
  const threshold = settings.threshold || THRESHOLD_DEFAULT;
  const easing = settings.easing || EASING_DEFAULT;
  const snapStop = settings.snapStop || SNAP_STOP_DEFAULT;

  function checkScrollSpeed (value: number, axis: 'x' | 'y') {
    const clear = () => {
      lastScrollValue[axis] = undefined;
    };

    const newValue = value;
    let delta;

    if (lastScrollValue[axis] !== undefined) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      delta = newValue - lastScrollValue[axis];
    } else {
      delta = 0;
    }

    lastScrollValue[axis] = newValue;
    if (scrollSpeedTimer) {
      clearTimeout(scrollSpeedTimer);
    }
    scrollSpeedTimer = window.setTimeout(clear, 100);

    return delta;
  }

  function bindElement (elem: HTMLElement) {
    target = elem;
    listenerElement = elem === document.documentElement ? window : elem;

    listenerElement.addEventListener('scroll', startAnimation, false);
    snapLengthUnit = parseSnapCoordinatesValue(snapDestinationX, snapDestinationY);

    requestAnimationFrame(() => {
      setInitialSnapDestinationElement();
    });
  }

  function setInitialSnapDestinationElement () {
    if (snapDestinationYElements) {
      const initialElement = detectDestinationElementCurrentlyInViewport();
      log('initialElement: ', initialElement);
      setCurrentSnapDestinationElement(initialElement);
    }
  }

  function unbindElement () {
    listenerElement.removeEventListener('scroll', startAnimation, false);
  }

  function startAnimation () {
    speedDeltaX = checkScrollSpeed(target.scrollLeft, 'x');
    if (target.scrollTop > 0) {
      speedDeltaY = checkScrollSpeed(target.scrollTop, 'y');
    }
    if (animating || (speedDeltaX === 0 && speedDeltaY === 0)) {
      return;
    }

    handler(target);
  }

  /**
   * scroll handler
   * this is the callback for scroll events.
   */
  function handler (targetElem: HTMLElement) {
    // if currently animating, stop it. this prevents flickering.
    if (animationFrame) {
      clearTimeout(animationFrame);
    }

    // if a previous timeout exists, clear it.
    if (scrollHandlerTimer) {
      // we only want to call a timeout once after scrolling..
      clearTimeout(scrollHandlerTimer);
    } else {
      scrollStart = {
        y: targetElem.scrollTop,
        x: targetElem.scrollLeft,
      };
    }

    scrollHandlerTimer = window.setTimeout(animationHandler, timeout);
  }

  function animationHandler () {
    // if we don't move a thing, we can ignore the timeout: if we did, there would be another timeout added for scrollStart+1.
    if (scrollStart.y === target.scrollTop && scrollStart.x === target.scrollLeft) {
      // ignore timeout
      return;
    }

    // detect direction of scroll. negative is up, positive is down.
    const direction = {
      y: Math.sign(speedDeltaY),
      x: Math.sign(speedDeltaX),
    };

    // get the next snap-point to snap-to
    const snapPoint = !snapDestinationYElements ? getNextSnapPoint(target, direction) : getNextSnapPointViaDestinationElements(target, direction);
    if (!snapPoint) {
      return;
    }

    // when scrolled too far too quickly, do not snap, instead reset and start from initial state
    const maxAnimationDistance = (window.innerHeight || document.documentElement.clientHeight) * 2.5;
    if ((snapPoint.y && Math.abs(snapPoint.y - target.scrollTop) > maxAnimationDistance)
      || (snapPoint.x && Math.abs(snapPoint.x - target.scrollLeft) > maxAnimationDistance)) {
      setInitialSnapDestinationElement();
      return;
    }

    listenerElement.removeEventListener('scroll', startAnimation, false);
    animating = true;

    // smoothly move to the snap point
    smoothScroll(target, snapPoint, () => {
      // after moving to the snap point, rebind the scroll event handler
      animating = false;
      listenerElement.addEventListener('scroll', startAnimation, { passive: true });
      onAnimationEnd();

      // reset scrollStart
      scrollStart = {
        y: target.scrollTop,
        x: target.scrollLeft,
      };
    });
  }

  /**
   * Calculate next snap point based on snapLength, calculated from snapDestinationX /
   * snapDestinationY.
   *
   * @param targetElem
   * @param direction
   */
  function getNextSnapPoint (targetElem: HTMLElement, direction: Coordinates) {
    if (direction.y === undefined) {
      direction.y = 0;
    }
    if (direction.x === undefined) {
      direction.x = 0;
    }

    // get snap length
    const snapLength = {
      y: Math.round(getYSnapLength(targetElem, snapLengthUnit.y)),
      x: Math.round(getXSnapLength(targetElem, snapLengthUnit.x)),
    };
    const top = targetElem.scrollTop;
    const left = targetElem.scrollLeft;

    const startPoint = {
      y: (scrollStart.y !== undefined ? scrollStart.y : 0) / snapLength.y || 0,
      x: (scrollStart.x !== undefined ? scrollStart.x : 0) / snapLength.x || 0,
    };
    const currentPoint = {
      y: top / snapLength.y || 0,
      x: left / snapLength.x || 0,
    };
    const nextPoint = {
      y: 0,
      x: 0,
    };

    /**
     * Set target and bounds by direction,
     * if threshold has not been reached, scroll back to currentPoint
     */
    if (isAboveThreshold(direction.y, currentPoint.y)) {
      if (snapStop) {
        nextPoint.y = roundByDirection(-direction.y, startPoint.y + direction.y);
      } else {
        nextPoint.y = roundByDirection(direction.y, currentPoint.y);
      }
    } else {
      nextPoint.y = roundByDirection(direction.y * -1, currentPoint.y);
    }

    if (isAboveThreshold(direction.x, currentPoint.x)) {
      if (snapStop) {
        nextPoint.x = roundByDirection(-direction.x, startPoint.x + direction.x);
      } else {
        nextPoint.x = roundByDirection(direction.x, currentPoint.x);
      }
    } else {
      nextPoint.x = roundByDirection(direction.x * -1, currentPoint.x);
    }

    // calculate where to scroll
    const scrollTo = {
      y: nextPoint.y * snapLength.y,
      x: nextPoint.x * snapLength.x,
    };

    // stay in bounds (minimum: 0, maximum: absolute height)
    scrollTo.y = stayInBounds(0, target.scrollHeight, scrollTo.y);
    scrollTo.x = stayInBounds(0, target.scrollWidth, scrollTo.x);

    return scrollTo;
  }

  /**
   * Calculate next snap point based on giveb destination elements. Currently only implemented for
   * vertical scrolling. (y-axis only)
   *
   * @param targetElem
   * @param direction
   */
  function getNextSnapPointViaDestinationElements (targetElem: HTMLElement, direction: Coordinates) {
    if (direction.y === undefined) {
      direction.y = 0;
    }
    if (direction.x === undefined) {
      direction.x = 0;
    }

    const top = targetElem.scrollTop;
    const currentElement = currentSnapDestinationElement;
    log('currentElement: ', currentElement);

    // calculate where to scroll
    let scrollTo;
    if (currentElement) {
      // calculate based on current element, scroll from one element to the prev/next element
      // extra-threshold explained: if element is higher than viewport, allow scrolling within one
      // element (used for stage with overflowing co2 labeling on mobile)
      const thresholdDistanceBase = document.documentElement.clientHeight * threshold;
      const extraThresholdAttr = currentElement.getAttribute('extra-threshold');
      const extraThreshold = extraThresholdAttr ? parseInt(extraThresholdAttr, 10) : 0;
      const thresholdDistance = thresholdDistanceBase + extraThreshold;
      const currentDistance = Math.abs(currentElement.getBoundingClientRect().top);
      if (currentDistance > thresholdDistance) {
        // above threshold ...
        let nextElement;
        if (direction.y !== undefined && direction.y > 0) {
          nextElement = nextSnapDestinationElement;
        } else if (direction.y !== undefined && direction.y < 0) {
          nextElement = prevSnapDestinationElement;
        }
        if (nextElement) {
          log('nextElement: ', nextElement);
          // ... scroll to next element
          scrollTo = {
            y: top + nextElement.getBoundingClientRect().top,
            x: 0,
          };
          setCurrentSnapDestinationElement(nextElement);
        } else {
          // ... no next element found, allow free scrolling
          scrollTo = undefined;
          setCurrentSnapDestinationElement(undefined);
        }
      } else {
        // not above threshold, snap back to current element
        // eslint-disable-next-line no-lonely-if
        if (top <= 72 && direction.y !== undefined && direction.y < 0) {
          // at the top of the page, do not snap in to first stage element when scrolling backwards:
          // allow scrolling completely to top, this way allow expending/collapsing of header
          // by scrolling a bit up and down at the very top of the page
          scrollTo = undefined;
        } else if (extraThreshold && direction.y !== undefined && direction.y > 0
          && currentDistance < extraThreshold) {
          // do not snap back if extra-threshold was given, allow free scrolling within
          // extra-threshold area
          scrollTo = undefined;
        } else {
          // snap back
          // eslint-disable-next-line no-lonely-if
          if (extraThreshold && direction.y !== undefined && direction.y > 0) {
            // for elements overflowing viewport, snap back only to bottom if scrolling forwards
            scrollTo = {
              y: top + currentElement.getBoundingClientRect().top + extraThreshold,
              x: 0,
            };
          } else {
            // normal snap back
            scrollTo = {
              y: top + currentElement.getBoundingClientRect().top,
              x: 0,
            };
          }
        }
      }
    } else {
      // calculate based on free scrolling, snap in next element when it is entering the viewport
      const enteringElement = detectDestinationElementEnteringViewport(direction.y);
      if (enteringElement) {
        log('enteringElement', enteringElement);
        scrollTo = {
          y: top + enteringElement.getBoundingClientRect().top,
          x: 0,
        };
        setCurrentSnapDestinationElement(enteringElement);
      }
    }
    // stay in bounds (minimum: 0, maximum: absolute height)
    if (scrollTo) {
      scrollTo.y = stayInBounds(0, target.scrollHeight, scrollTo.y);
      scrollTo.x = stayInBounds(0, target.scrollWidth, scrollTo.x);
    }

    return scrollTo;
  }

  function setCurrentSnapDestinationElement (elem: HTMLElement | undefined) {
    currentSnapDestinationElement = elem;
    if (elem !== undefined) {
      prevSnapDestinationElement = findPrevSnapDestinationElement(elem);
      nextSnapDestinationElement = findNextSnapDestinationElement(elem);
    } else {
      prevSnapDestinationElement = undefined;
      nextSnapDestinationElement = undefined;
    }
  }

  function findPrevSnapDestinationElement (elem: HTMLElement): HTMLElement | undefined {
    if (snapDestinationYElements && currentSnapDestinationElement) {
      for (let i = 0; i < snapDestinationYElements.length; i += 1) {
        if (snapDestinationYElements[i] === elem) {
          if (i >= 1 && snapDestinationYElements[i - 1] !== undefined) {
            const prevElement = snapDestinationYElements[i - 1];
            const tolerance = (window.innerHeight || document.documentElement.clientHeight) * 0.5;
            if (Math.abs(currentSnapDestinationElement.getBoundingClientRect().top - prevElement.getBoundingClientRect().bottom) > tolerance) {
              // more than 50% of viewport height distance between the two elements, do not directly
              // snap to next element, switch to normal scrolling instead
              log('findPrev - too far away - switching to normal scrolling');
              return undefined;
            }
            return prevElement;
          }
        }
      }
    }
    return undefined;
  }

  function findNextSnapDestinationElement (elem: HTMLElement): HTMLElement | undefined {
    if (snapDestinationYElements && currentSnapDestinationElement) {
      for (let i = 0; i < snapDestinationYElements.length; i += 1) {
        if (snapDestinationYElements[i] === elem) {
          if (snapDestinationYElements[i + 1] !== undefined) {
            const nextElement = snapDestinationYElements[i + 1];
            const tolerance = (window.innerHeight || document.documentElement.clientHeight) * 0.5;
            if (Math.abs(nextElement.getBoundingClientRect().top - currentSnapDestinationElement.getBoundingClientRect().bottom) > tolerance) {
              // more than 50% of viewport height distance between the two elements, do not directly
              // snap to next element, switch to normal scrolling instead
              log('findNext - too far away - switching to normal scrolling');
              return undefined;
            }
            return nextElement;
          }
        }
      }
    }
    return undefined;
  }

  function detectDestinationElementCurrentlyInViewport (): HTMLElement | undefined {
    for (let i = 0; snapDestinationYElements && i < snapDestinationYElements.length; i += 1) {
      const elem = snapDestinationYElements[i];
      if (isDestinationElementCurrentlyInViewport(elem)) {
        return elem;
      }
    }
    return undefined;
  }

  function isDestinationElementCurrentlyInViewport (elem: HTMLElement): boolean {
    const rect = elem.getBoundingClientRect();
    const tolerance = (window.innerHeight || document.documentElement.clientHeight) * 0.5;
    return (
      rect.top >= -tolerance && rect.top <= tolerance
    );
  }

  function detectDestinationElementEnteringViewport (direction: number | undefined): HTMLElement | undefined {
    for (let i = 0; snapDestinationYElements && i < snapDestinationYElements.length; i += 1) {
      const elem = snapDestinationYElements[i];
      if (isDestinationElementEnteringViewport(elem, direction)) {
        return elem;
      }
    }
    return undefined;
  }

  function isDestinationElementEnteringViewport (elem: HTMLElement, direction: number | undefined): boolean {
    const rect = elem.getBoundingClientRect();
    const tolerance = (window.innerHeight || document.documentElement.clientHeight) * 0.5;
    return (
      // scrolling down, element entering viewport from the bottom
      (direction !== undefined && direction > 0 && rect.top > 0 && rect.top <= tolerance)
      // scrolling down, element entering viewport from the top
      || (direction !== undefined && direction < 0 && rect.top < 0 && rect.top >= -tolerance)
    );
  }

  function isAboveThreshold (direction: number | undefined, value: number): boolean {
    if (direction === undefined) {
      return false;
    }
    return direction > 0 ? value % 1 > threshold : 1 - (value % 1) > threshold;
  }

  function roundByDirection (direction: number | undefined, value: number) {
    if (direction !== undefined && direction === -1) {
      // when we go up, we floor the number to jump to the next snap-point in scroll direction
      return Math.floor(value);
    }
    // go down, we ceil the number to jump to the next in view.
    return Math.ceil(value);
  }

  function stayInBounds (min: number, max: number, destined: number) {
    return Math.max(Math.min(destined, max), min);
  }

  function parseSnapCoordinatesValue (
    x: Settings['snapDestinationX'],
    y: Settings['snapDestinationY'],
  ) {
    // regex to parse lengths
    const regex = /([+-]?(?=\.\d|\d)(?:\d+)?(?:\.?\d*)(?:[eE][+-]?\d+)?)(px|%|vw|vh)/;
    // defaults
    const parsed = {
      y: {
        value: 0,
        unit: 'px',
      },
      x: {
        value: 0,
        unit: 'px',
      },
    };

    if (typeof y === 'number') {
      parsed.y.value = y;
    } else {
      const resultY = regex.exec(y as string);
      if (resultY !== null) {
        parsed.y = {
          value: Number(resultY[1]),
          unit: resultY[2],
        };
      }
    }

    if (typeof x === 'number') {
      parsed.x.value = x;
    } else {
      const resultX = regex.exec(x as string);
      if (resultX !== null) {
        parsed.x = {
          value: Number(resultX[1]),
          unit: resultX[2],
        };
      }
    }

    return parsed;
  }

  function getYSnapLength (obj: HTMLElement, declaration: SnapLength) {
    // get y snap length based on declaration unit
    if (declaration.unit === 'vh') {
      return (
        (Math.max(document.documentElement.clientHeight, window.innerHeight || 1) / 100)
        * declaration.value
      );
    }
    if (declaration.unit === '%') {
      return (obj.clientHeight / 100) * declaration.value;
    }
    return declaration.value;
  }

  function getXSnapLength (obj: HTMLElement, declaration: SnapLength) {
    // get x snap length based on declaration unit
    if (declaration.unit === 'vw') {
      return (
        (Math.max(document.documentElement.clientWidth, window.innerWidth || 1) / 100)
        * declaration.value
      );
    }
    if (declaration.unit === '%') {
      return ((obj.clientWidth / 100) * declaration.value);
    }
    return declaration.value;
  }

  function isEdge (Coordinates: Coordinates) {
    return (Coordinates.x === 0 && speedDeltaY === 0) || (Coordinates.y === 0 && speedDeltaX === 0);
  }

  function smoothScroll (obj: HTMLElement, endCoordinates: Coordinates, onSmoothScrollEnd: (...args: any) => void) {
    const position = (start: number, end: number, elapsed: number, period: number): number => {
      if (elapsed > period) {
        return end;
      }

      return start + (end - start) * easing(elapsed / period);
    };

    const startCoordinates = {
      y: obj.scrollTop,
      x: obj.scrollLeft,
    };

    // get animation frame or a fallback
    const currentPeriod = isEdge(startCoordinates) ? 1 : duration;
    let startTime: number;

    // set up the stepping function
    function step (timestamp: number) {
      if (!startTime) {
        startTime = timestamp;
      }
      const currentElapsed = timestamp - startTime;

      // change position on y-axis if result is a number.
      if (endCoordinates.y !== undefined && !Number.isNaN(endCoordinates.y)) {
        obj.scrollTop = position(startCoordinates.y, endCoordinates.y, currentElapsed, currentPeriod);
      }

      // change position on x-axis if result is a number.
      if (endCoordinates.x !== undefined && !Number.isNaN(endCoordinates.x)) {
        obj.scrollLeft = position(startCoordinates.x, endCoordinates.x, currentElapsed, currentPeriod);
      }

      // check if we are over due;
      if (currentElapsed < currentPeriod) {
        requestAnimationFrame(step);
      } else if (typeof onSmoothScrollEnd === 'function') {
        // is there a callback? stop execution and run the callback
        onSmoothScrollEnd(endCoordinates);
      }
    }
    animationFrame = requestAnimationFrame(step);
  }

  function log (...parameters: any[]) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log.apply(null, ['scroll-snap'].concat(parameters));
    }
  }

  function bind () {
    bindElement(element);
  }

  function unbind () {
    unbindElement();
  }

  function addSnapDestinationYElements (elements: HTMLElement[]) {
    snapDestinationYElements = snapDestinationYElements || [];
    let updated = false;
    elements.forEach((elem) => {
      if (snapDestinationYElements && !snapDestinationYElements.includes(elem)) {
        snapDestinationYElements.push(elem);
        updated = true;
      }
    });
    if (updated) {
      removeObsoleteSnapDestinationYElements();
      sortSnapDestinationYElements();
      requestAnimationFrame(() => {
        setInitialSnapDestinationElement();
      });
    }
  }

  function removeObsoleteSnapDestinationYElements () {
    if (snapDestinationYElements) {
      snapDestinationYElements.forEach((elem: HTMLElement, index: number) => {
        if (!ShadowDom.isInDocument(elem)) {
          (snapDestinationYElements as HTMLElement[]).splice(index);
        }
      });
    }
  }

  function sortSnapDestinationYElements () {
    if (snapDestinationYElements) {
      snapDestinationYElements.sort((a: HTMLElement, b: HTMLElement) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
    }
  }

  bind();

  return {
    log,
    bind,
    unbind,
    addSnapDestinationYElements,
  };
}
